import { Link, useNavigate } from "react-router-dom";
import {
  Divider, ErrorMessages, IconButton, IconButtonType, Spinner
} from "@whitespace/whitespace-ui";
import {
  Alert,
  Breadcrumb, Button, Card, Col, Form, ListGroup, Row
} from "react-bootstrap";
import { useEffect, useState } from "react";

import { useAmplifyREST } from "hooks/useAmplifyREST";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { useStateMachine } from "little-state-machine";
import { updateSelectedUser } from "actions/updateSelectedUser";
import { updateIsUserHistoryFilterDrawerOpen } from "actions/updateIsUserHistoryFilterDrawerOpen";
import { UserStatusTypeValue, UserStatusType } from "interfaces/models/UserStatusType";
import { UserIcon } from "components/UserIcon/UserIcon";

import "./ManageUser.scss";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";
import { UserPasswordStatus } from "interfaces/models/UserPasswordStatus";
import { mapUserResponseItemToUser } from "mappers/mapUserResponseItemToUser/mapUserResponseItemToUser";
import { validateInternationalPhoneNumber } from "modules/regExHelpers";
import { convertToInternationalNumber, stripInternationalDiallingCode } from "modules/phoneUtils";
import { yupResolver } from "@hookform/resolvers/yup";
import { ariaInvalidCheck, normaliseInteger } from "@whitespace/whitespace-utils";
import IUpdateUserRequest from "interfaces/requests/IUpdateUserRequest";
import AmplifyError from "components/AmplifyError/AmplifyError";

const ManageUser = () => {
  const { state } = useStateMachine();
  const navigate = useNavigate();
  const { actions } = useStateMachine({
    updateIsUserHistoryFilterDrawerOpen,
    updateSelectedUser
  });

  const {
    isSending: changeUserStatusIsSending,
    errors: changeUserStatusErrors, statusCode: changeUserStatusCode, sendRequest: changeUserStatusSendRequest, resetState: changeUserStatusResetState
  } = useAmplifyREST();

  const {
    isSending: updateUserIsSending,
    errors: updateUserErrors, statusCode: updateUserStatusCode, sendRequest: updateUserSendRequest, resetState: updateUserResetState
  } = useAmplifyREST();

  const {
    isSending: getUserIsSending, data: getUserData,
    errors: getUserErrors, sendRequest: getUserSendRequest, resetState: getUserResetState
  } = useAmplifyREST();

  const {
    isSending: updateUserAdminStatusIsSending, statusCode: updateUserAdminStatusCode,
    errors: updateUserAdminStatusErrors, sendRequest: updateUserAdminStatusSendRequest, resetState: updateUserAdminStatusResetState
  } = useAmplifyREST();

  const {
    isSending: resetPasswordIsSending,
    errors: resetPasswordErrors, statusCode: resetPasswordStatusCode, sendRequest: resetPasswordSendRequest, resetState: resetPasswordResetState
  } = useAmplifyREST();

  const {
    isSending: resendPasswordIsSending,
    errors: resendPasswordErrors, statusCode: resendPasswordStatusCode, sendRequest: resendPasswordSendRequest, resetState: resendPasswordResetState
  } = useAmplifyREST();

  const {
    isSending: forceReAuthenticationIsSending,
    errors: forceReAuthenticationErrors, statusCode: forceReAuthenticationStatusCode, sendRequest: forceReAuthenticationSendRequest,
    resetState: forceReAuthenticationResetState
  } = useAmplifyREST();

  const [showForceUserReAuthenticationMessage, setShowForceUserReAuthenticationMessage] = useState(false);

  const apiErrors = changeUserStatusErrors || updateUserErrors || getUserErrors || updateUserAdminStatusErrors
  || resetPasswordErrors || resendPasswordErrors || forceReAuthenticationErrors;

  const isSending = changeUserStatusIsSending || updateUserIsSending || getUserIsSending || updateUserAdminStatusIsSending
  || resetPasswordIsSending || resendPasswordIsSending || forceReAuthenticationIsSending;

  const schema = yup.object().shape({
    name: yup.string().trim()
      .required("Full Name is required"),
    email: yup.string().trim()
      .required("Email Address is required")
      .email("Invalid Email Address"),
    phone: yup.string().trim()

      .test(
        "valid international number",
        "Invalid Mobile Number",
        (val :string | undefined) => val!.length === 0 || validateInternationalPhoneNumber(convertToInternationalNumber(val!))
      )

  });

  const {
    formState: { errors }, register, handleSubmit, setValue
  } = useForm<IUpdateUserRequest>({
    resolver: yupResolver(schema),
    shouldFocusError: true,
    mode: "onSubmit",
    reValidateMode: "onBlur"
  });

  const handleNavToUsers = () => {
    actions.updateSelectedUser(undefined);
    navigate("/users");
  };

  const resetStates = () => {
    changeUserStatusResetState();
    getUserResetState();
    updateUserResetState();
    updateUserAdminStatusResetState();
    resetPasswordResetState();
    resendPasswordResetState();
    forceReAuthenticationResetState();
    setShowForceUserReAuthenticationMessage(false);
  };

  const handleChangeUserStatus = () => {
    resetStates();
    const body = {
      enabled: state.selectedUser!.status === UserStatusType.Inactive
    };
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/status`;
    changeUserStatusSendRequest(url, "PUT", body);
  };

  useEffect(() => {
    if (changeUserStatusCode === 204) {
      const selectedUser = { ...state.selectedUser! };
      selectedUser.status = selectedUser.status === UserStatusType.Active ? UserStatusType.Inactive : UserStatusType.Active;
      actions.updateSelectedUser(selectedUser);
    }
  }, [changeUserStatusCode]);

  const handleUpdateUserClick = (request : IUpdateUserRequest) => {
    resetStates();
    request.phone = convertToInternationalNumber(request.phone);
    request.admin = state.selectedUser!.isPortalAdmin ?? false;
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}`;
    updateUserSendRequest(url, "PUT", request);
  };

  const getSelectedUser = () => {
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}`;
    getUserSendRequest(url, "GET");
  };

  useEffect(() => {
    if (updateUserStatusCode === 204) {
      getSelectedUser();
    }
  }, [updateUserStatusCode]);

  useEffect(() => {
    if (getUserData) {
      const userData = mapUserResponseItemToUser(getUserData, []);
      actions.updateSelectedUser(userData);
      setValue("name", userData.username);
      setValue("email", userData.emailAddress);
      setValue("phone", stripInternationalDiallingCode(userData.mobileNumber!));
    }
  }, [getUserData]);

  const handleAdminChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetStates();
    const isAdmin = event.target.checked;
    const method = isAdmin ? "PUT" : "DELETE";
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/admin`;
    updateUserAdminStatusSendRequest(url, method);
  };

  useEffect(() => {
    if (updateUserAdminStatusCode === 204) {
      getSelectedUser();
    }
  }, [updateUserAdminStatusCode]);

  const handleResetPassword = () => {
    resetStates();
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/reset_password`;
    resetPasswordSendRequest(url, "PUT");
  };

  useEffect(() => {
    if (resetPasswordStatusCode === 204) {
      getSelectedUser();
    }
  }, [resetPasswordStatusCode]);

  const handleResendPassword = () => {
    resetStates();
    const request = {
      email: state.selectedUser!.emailAddress
    };
    resendPasswordSendRequest("users/invite", "POST", request);
  };
  useEffect(() => {
    if (resendPasswordStatusCode === 204) {
      getSelectedUser();
    }
  }, [resendPasswordStatusCode]);

  const handleForceReauthentication = () => {
    resetStates();
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/sign_out`;
    forceReAuthenticationSendRequest(url, "PUT");
  };

  useEffect(() => {
    if (forceReAuthenticationStatusCode === 204) {
      getSelectedUser();
      setShowForceUserReAuthenticationMessage(true);
    }
  }, [forceReAuthenticationStatusCode]);

  useEffect(() => {
    getSelectedUser();
  }, []);

  return (
    <>
      {apiErrors && <AmplifyError errors={apiErrors} />}
      {
        showForceUserReAuthenticationMessage
        && (
          <Alert
            variant="info"
            role="alert"
            className="margin-medium"
            aria-atomic="true"
            onClose={() => setShowForceUserReAuthenticationMessage(false)}
            dismissible
          >
            Re-authentication request sent
          </Alert>
        )
      }
      {state.selectedUser
      && (
        <>
          <Card className="card-user-management">
            <Card.Header>
              <Row className="w-100">
                <Col xs={8}>
                  <h1>
                    Manage User
                    <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
                  </h1>
                </Col>
                <Col xs={4} className="d-flex justify-content-end">
                  <span className="selected-user-title">{state.selectedUser?.username}</span>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="no-padding">
              <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
                <Col xs={8} className="d-flex">
                  <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                    <Breadcrumb.Item data-testid="breadcrumb-users" linkAs={Link} linkProps={{ to: "/" }} onClick={handleNavToUsers}>Users</Breadcrumb.Item>
                    <Breadcrumb.Item active>Manage User</Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
                <Col xs={4} className="d-flex justify-content-end user-filter">
                  <Button variant="primary" onClick={handleNavToUsers}>
                    Back
                  </Button>
                </Col>
              </Row>
              <Divider />
              <Row className="d-flex top-margin-small bottom-margin-small">
                <Col xs={12} lg={6}>
                  <div className="user-details-section">
                    <Form autoComplete="off" onSubmit={handleSubmit(handleUpdateUserClick)}>
                      <Row className="mb-2">
                        <Form.Group>
                          <Form.Label className="horiz-form-label">Full Name</Form.Label>
                          <Form.Control
                            {...register("name")}
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-invalid="false"
                            aria-label="Full Name"
                            defaultValue={state.selectedUser.username}
                            data-testid="fullNameInput"
                            disabled={state.selectedUser.isFederatedUser}
                          />
                        </Form.Group>
                        <ErrorMessages fieldName="name" errors={errors} validationFailures={null} />
                      </Row>
                      <Row className="mb-2">
                        <Form.Group>
                          <Form.Label className="horiz-form-label">Email Address</Form.Label>
                          <Form.Control
                            {...register("email")}
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-invalid="false"
                            aria-label="Email Address"
                            defaultValue={state.selectedUser?.emailAddress}
                            data-testid="emailAddressInput"
                            disabled={state.selectedUser.isFederatedUser}
                          />
                          <ErrorMessages fieldName="email" errors={errors} validationFailures={null} />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group controlId="phone">
                          <Form.Label className="horiz-form-label">Mobile Number</Form.Label>
                          <div>
                            <div className="d-flex align-items-center">
                              <span className="d-inline-flex horiz-form-label mb-0 pe-1">+44</span>
                              <Form.Control
                                {...register("phone")}
                                data-testid="mobileNumberInput"
                                type="text"
                                role="textbox"
                                className="d-inline-flex"
                                aria-required="true"
                                aria-invalid={ariaInvalidCheck("phone", errors, null)}
                                aria-label="Mobile Number"
                                maxLength={13}
                                disabled={state.selectedUser.isFederatedUser}
                                defaultValue={stripInternationalDiallingCode(state.selectedUser.mobileNumber!)}
                                onChange={e => { e.target.value = normaliseInteger(e.target.value); }}
                              />
                            </div>
                            <ErrorMessages fieldName="phone" errors={errors} validationFailures={null} />
                          </div>
                        </Form.Group>
                      </Row>
                      {!state.selectedUser.isFederatedUser
                      && (
                        <Row className="mt-2">
                          <Col xs={12} className="d-flex justify-content-center">
                            <IconButton
                              iconType={IconButtonType.Save}
                              variant="primary"
                              className="right-margin-small"
                              disabled={updateUserIsSending}
                              aria-label="Update User Details"
                              aria-disabled={updateUserIsSending}
                              type="submit"
                              spin={updateUserIsSending}
                            >
                              Update
                            </IconButton>
                          </Col>
                        </Row>
                      )}
                    </Form>
                  </div>
                  <Divider />
                  <Row className="mb-2 mt-3">
                    <Col className="left-margin-medium">
                      <Form.Group controlId="isPortalAdminUser" className="d-flex align-items-center">
                        <span className="me-3"><UserIcon isFederatedUser={false} isPortalAdminUser /></span>
                        <Form.Label className="d-inline-flex horiz-form-label w-auto mb-0 me-3">Portal Admin User</Form.Label>
                        <Form.Check
                          aria-label="Portal Admin User?"
                          className="d-inline-flex custom-switch custom-switch-md ms-2"
                          type="switch"
                          defaultChecked={state.selectedUser.isPortalAdmin}
                          aria-required="false"
                          disabled={updateUserAdminStatusIsSending}
                          onChange={handleAdminChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
                <div className="d-sm-block d-lg-none mt-4 mb-4">
                  <Divider />
                </div>
                <Col xs={12} lg={6}>
                  {state.selectedUser.isFederatedUser
              && (
                <Row className="m-0">
                  <div className="d-flex align-items-center federated-user-section">
                    <Col className="d-flex title">Federated User</Col>
                    <Col className="d-flex justify-content-end ad-name">{state.selectedUser.federatedADName}</Col>
                  </div>
                </Row>
              )}
                  <Row className="list-heading">
                    <Col xs={5}>Options</Col>
                    <Col xs={5}>Details</Col>
                    <Col xs={2} className="d-flex justify-content-center align-self-center">Actions</Col>
                  </Row>
                  <ListGroup variant="flush" className="striped highlight user-options">
                    <ListGroup.Item>
                      <Row className="align-items-center">
                        <Col xs={5}>Groups</Col>
                        <Col xs={5}>Edit, Add, Remove</Col>
                        <Col xs={2} className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            className="right-margin-small"
                            aria-label="Manage User Groups"
                            onClick={() => navigate("/manageusergroups")}
                          >
                            Manage
                          </Button>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row className="align-items-center">
                        <Col xs={5}>Applications</Col>
                        <Col xs={5}>Edit, Add, Remove</Col>
                        <Col xs={2} className="d-flex justify-content-end">
                          <Button
                            variant={state.selectedUser!.applicationCount! > 0 ? "primary" : "danger"}
                            className="right-margin-small"
                            aria-label={state.selectedUser!.applicationCount! > 0 ? "Manage Applications" : "Fix Applications"}
                            onClick={() => navigate("/manageuserapplications")}
                          >
                            {state.selectedUser!.applicationCount! > 0 ? "Manage" : "Fix"}
                          </Button>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    {!state.selectedUser.isFederatedUser
                && (
                  <>
                    <ListGroup.Item>
                      <Row className="align-items-center">
                        <Col xs={5}>Password Reset</Col>
                        <Col xs={5} data-testid="lastModifiedTS">
                          {state.selectedUser.passwordStatus! === UserPasswordStatus.ResetRequired
                          && <span>Force Change Password Requested</span>}

                          {state.selectedUser.passwordStatus! !== UserPasswordStatus.ResetRequired && state.selectedUser.lastModifiedDate
                            && format(new Date(state.selectedUser.lastModifiedDate), "dd/MM/yyyy HH:mm:ss", { locale: enGB })}

                        </Col>
                        <Col xs={2} className="d-flex justify-content-end">
                          {state.selectedUser.passwordStatus! === UserPasswordStatus.ResetRequired && (
                            <Button
                              data-testid="btnPasswordResetRequested"
                              variant="primary"
                              className="right-margin-small"
                              aria-label="Password Reset Requested"
                              disabled
                            >
                              Requested
                            </Button>
                          )}

                          {![UserPasswordStatus.ResetRequired, UserPasswordStatus.ForceChangePassword].includes(state.selectedUser.passwordStatus!) && (
                            <Button
                              variant="primary"
                              className="right-margin-small"
                              aria-label="Reset Password"
                              onClick={() => handleResetPassword()}
                              disabled={resetPasswordIsSending || !state.selectedUser.adminCanResetPassword}
                            >
                              Reset
                            </Button>
                          )}
                          {state.selectedUser.passwordStatus! === UserPasswordStatus.ForceChangePassword && (
                            <Button
                              variant="primary"
                              className="right-margin-small"
                              aria-label="Resend Password"
                              onClick={() => handleResendPassword()}
                              disabled={resendPasswordIsSending}
                            >
                              Resend
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row className="align-items-center">
                        <Col xs={5}>Force User Re-Authentication</Col>
                        <Col xs={5} data-testid="lastAuthenticatedTS">
                          {state.selectedUser.lastAuthenticatedDate
                            && format(new Date(state.selectedUser.lastAuthenticatedDate), "dd/MM/yyyy HH:mm:ss", { locale: enGB })}

                        </Col>
                        <Col xs={2} className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            className="right-margin-small"
                            aria-label="Reset User Authentication"
                            onClick={() => handleForceReauthentication()}
                            disabled={forceReAuthenticationIsSending}
                          >
                            Reset
                          </Button>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row className="align-items-center">
                        <Col xs={5}>User MFA Options</Col>
                        <Col xs={5}>SMS, App</Col>
                        <Col xs={2} className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            className="right-margin-small"
                            aria-label="Manage User MFA"
                            onClick={() => navigate("/manageusermfaoptions")}
                          >
                            Manage
                          </Button>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  </>
                ) }
                    <ListGroup.Item>
                      <Row className="align-items-center">
                        <Col xs={5}>User Status</Col>
                        <Col xs={5} data-testid="lblUserStatus">{UserStatusTypeValue[state.selectedUser.status!]}</Col>
                        <Col xs={2} className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            className="right-margin-small"
                            aria-label={state.selectedUser!.status === UserStatusType.Inactive ? "Activate User" : "Deactivate User"}
                            onClick={() => handleChangeUserStatus()}
                            disabled={changeUserStatusIsSending}
                          >
                            {state.selectedUser.status === UserStatusType.Inactive ? "Activate" : "Deactivate"}
                          </Button>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  </ListGroup>
                </Col>
              </Row>
              <Divider className="no-display-sm" />
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

export default ManageUser;
